"use client";

import Image from "next/image";

/**
 * Eligibility 1041 — BDU Eligibility Criteria.
 *
 * Port of BDU v1 `components/organisms/Eligibility.tsx`: a heading with a gradient
 * rule and a single tinted panel holding a leading icon and the criteria copy.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * COLUMN REUSE — the `eligibility` table carries no gradient pair of its own, so the
 * heading rule is driven by the existing `background_gradient_*` trio; the panel uses
 * `background_color` + `description_text_color`, and its glyph is `button_icon`. No
 * new column is added for this variant.
 *
 * ICON — v1 uses a static `/icons/user.svg` profile glyph. Here it is a CMS upload
 * (`button_icon`) so each programme page can pick its own, falling back to the same
 * glyph inline. The fallback is drawn with `currentColor` and tinted with
 * `description_text_color`, so it always matches the copy beside it (v1's asset is a
 * fixed #9F1C33, which would clash with a CMS-chosen panel colour).
 */

/** Profile glyph from v1's `/icons/user.svg`, recoloured via `currentColor`. */
function UserGlyph({ color }: { color: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ color }}
    >
      <path
        d="M9.99984 9.99996C8.7165 9.99996 7.61789 9.54301 6.704 8.62913C5.79012 7.71524 5.33317 6.61663 5.33317 5.33329C5.33317 4.04996 5.79012 2.95135 6.704 2.03746C7.61789 1.12357 8.7165 0.666626 9.99984 0.666626C11.2832 0.666626 12.3818 1.12357 13.2957 2.03746C14.2096 2.95135 14.6665 4.04996 14.6665 5.33329C14.6665 6.61663 14.2096 7.71524 13.2957 8.62913C12.3818 9.54301 11.2832 9.99996 9.99984 9.99996ZM0.666504 19.3333V16.0666C0.666504 15.4055 0.836837 14.7981 1.1775 14.2443C1.51817 13.6905 1.97006 13.2674 2.53317 12.975C3.73873 12.3722 4.96373 11.9203 6.20817 11.6193C7.45262 11.3183 8.7165 11.1674 9.99984 11.1666C11.2832 11.1658 12.5471 11.3167 13.7915 11.6193C15.0359 11.9218 16.2609 12.3737 17.4665 12.975C18.0304 13.2666 18.4827 13.6897 18.8233 14.2443C19.164 14.7988 19.334 15.4063 19.3332 16.0666V19.3333H0.666504Z"
        fill="currentColor"
      />
    </svg>
  );
}

interface Eligibility1041Data {
  title?: string;
  title_text_color?: string;
  description?: string;
  description_text_color?: string;
  /** Criteria panel fill */
  background_color?: string;
  /** Heading rule */
  background_gradient_start_color?: string;
  background_gradient_end_color?: string;
  background_gradient_angle?: string;
  /** Leading glyph inside the panel */
  button_icon?: string;
  button_icon_alt?: string;
  status?: boolean;
}

const Eligibility1041 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as Eligibility1041Data | undefined;

  const {
    title = "",
    title_text_color = "#222023",
    description = "",
    description_text_color = "#FFFFFF",
    background_color = "#836537",
    background_gradient_start_color = "#FF0013",
    background_gradient_end_color = "#990013",
    background_gradient_angle = "90deg",
    button_icon = "",
    button_icon_alt = "",
  } = sectionData || {};

  if (!sectionData?.status || !description) return null;

  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex w-full flex-col gap-[32px] px-[20px] py-[36px] tab:px-[64px] tab:py-[48px]">
        <div className="flex flex-col gap-[16px]">
          <h2
            className="text-[24px] font-bold tab:text-[32px] tab:leading-[36px]"
            style={{ color: title_text_color }}
          >
            {title}
          </h2>
          <div
            className="h-[3px] w-[108px]"
            style={{
              backgroundImage: `linear-gradient(${background_gradient_angle}, ${background_gradient_start_color} 0%, ${background_gradient_end_color} 100%)`,
            }}
          />
        </div>

        <div
          className="flex gap-[10px] rounded-[8px] p-[24px]"
          style={{ backgroundColor: background_color }}
        >
          <div className="flex w-7 shrink-0 items-start justify-center">
            {button_icon ? (
              <Image
                src={button_icon}
                alt={button_icon_alt || "Eligibility"}
                width={28}
                height={28}
                loading="lazy"
              />
            ) : (
              <UserGlyph color={description_text_color} />
            )}
          </div>
          <p style={{ color: description_text_color }}>{description}</p>
        </div>
      </div>
    </div>
  );
};

export default Eligibility1041;
